import {apiGet} from "../api-call-helpers";
import {globalAuthManager} from "../GlobalServices";
import * as keys from 'keys'
import Position from "../../util/Position";
import {callPrice, putPrice} from "../../util/analytics/blackscholes";
import newtonRaphson from "../../util/analytics/newton-raphson";

/**
 * Linear interpolation between two points
 * @param {number} x - Target x value
 * @param {number} x1 - First x point
 * @param {number} y1 - First y point
 * @param {number} x2 - Second x point
 * @param {number} y2 - Second y point
 * @returns {number} - Interpolated value
 */
function linearInterpolate(x, x1, y1, x2, y2) {
    return y1 + ((x - x1) * (y2 - y1)) / (x2 - x1);
}

/**
 * Calculate forward price using linear interpolation for the specific tenor
 * @param {number} yrsToMaturity - Years to maturity
 * @param {Array<number>} tList - List of tenors in years 
 * @param {Array<number>} forwardList - List of forward prices corresponding to tenors
 * @returns {number} - Interpolated forward price
 */
function interpolateForward(yrsToMaturity, tList, forwardList) {
    // Handle empty inputs
    if (!tList || !forwardList || tList.length === 0) {
        throw new Error('Missing required forward curve data');
    }

    // Handle single point case
    if (tList.length === 1) {
        return forwardList[0];
    }

    // Find surrounding tenors
    let i1 = 0, i2 = 1;
    for (let i = 0; i < tList.length - 1; i++) {
        if (tList[i] <= yrsToMaturity && yrsToMaturity <= tList[i + 1]) {
            i1 = i;
            i2 = i + 1;
            break;
        }
    }

    // Handle extrapolation cases
    if (yrsToMaturity <= tList[0]) {
        i1 = 0;
        i2 = 1;
    } else if (yrsToMaturity >= tList[tList.length - 1]) {
        i1 = tList.length - 2;
        i2 = tList.length - 1;
    }

    // Interpolate between surrounding points
    return linearInterpolate(yrsToMaturity, tList[i1], forwardList[i1], tList[i2], forwardList[i2]);
}

// each forward point is a date-str and a forward price.  Convert the date-str to a number of years from now
function forwardPointsToTenors(forwardCurve) {
    return forwardCurve.map(fc => {
        const date = new Date(fc[0])
        const now = new Date()
        const yrs = (date - now) / (365.25 * 24 * 60 * 60 * 1000)
        return yrs
    })
}

let FORWARD_CACHE = {}

export function calcForwardFromMarketDataObj(ticker, marketDataObj, forwardDateObj) {
    // if forwardDate is a string, convert to a date object
    if (typeof forwardDateObj === 'string') {
        forwardDateObj = new Date(forwardDateObj)
    }

    const dateStr = forwardDateObj.toISOString().split('T')[0]
    const cacheKey = `${ticker}-${dateStr}`
    if (FORWARD_CACHE[cacheKey]) {
        console.log(`Forward price for ${ticker}-${dateStr} found in cache: ${FORWARD_CACHE[cacheKey]}`)
        return FORWARD_CACHE[cacheKey]
    }

    let forwardCurve = null
    try {
        const tickerCurveObj = marketDataObj.equity.forwards.find(f => f.ticker === ticker)
        forwardCurve = tickerCurveObj == null ? null : tickerCurveObj.forward_curve
    } catch (e) {
        console.error(`Error getting forward curve for ${ticker}-${dateStr}: ${e}`)
        return 1
    }

    if (forwardCurve == null) {
        return 1
    }

    const forwardPoints = forwardCurve.forward_points
    const tenors = forwardPointsToTenors(forwardPoints)
    const forwardPrices = forwardPoints.map(fp => fp[1])
    const yrsToMaturity = (forwardDateObj - new Date()) / (365.25 * 24 * 60 * 60 * 1000)
    let fwd = 1
    try {
        fwd = interpolateForward(yrsToMaturity, tenors, forwardPrices)
    } catch (e) {
        console.error(`Error calculating forward curve for ${ticker}-${dateStr}: ${e}`)
        return 1
    }
    if (!Number.isFinite(fwd)) {
        return 1
    }

    FORWARD_CACHE[cacheKey] = fwd
    console.log(`Forward price for ${ticker}-${dateStr} calculated: ${fwd}`)
    return fwd
}

export function calcUniqueTickers(posDicts) {
    const tickers = posDicts.map(posDict => {
        let posObj = new Position(posDict)
        return posObj.instrument.getTicker()
    })

    return Array.from(new Set(tickers.filter(ticker => ticker != null)))
}

function calcMarkedInstrumentVol(optionDict, optionPrice, marketDataObj, markAsOfDate) {
    const ticker = optionDict.ticker
    const [spotPriceObj] = marketDataObj.equity.spotPrices.filter(sp => sp.ticker === ticker)
    const spot = spotPriceObj.spotPrice
    const strike = optionDict.strike

    const parts = optionDict.expiration.split('-')
    const expirationObj = new Date(parts[0], parts[1] - 1, parts[2])
    const T = (expirationObj - markAsOfDate) / (365.25 * 24 * 60 * 60 * 1000)
    const df = Math.pow(.95, T)

    // get the forward from the market data object
    const fwdNormalized = calcForwardFromMarketDataObj(ticker, marketDataObj, expirationObj)
    const fwd = spot * fwdNormalized

    function putSolve(v) {
        return putPrice(fwd, strike, T, v, df) - optionPrice
    }

    function callSolve(v) {
        return callPrice(fwd, strike, T, v, df) - optionPrice
    }

    let f = putSolve
    if (optionDict.type.toLowerCase() === 'c') {
        f = callSolve
    }

    let guess = .2
    const nrOptions = {tolerance: .01, verbose: false}
    const nr = newtonRaphson(f, guess, nrOptions)

    if (nr === false) {
        return 0.23456789
    } else {
        return nr
    }
}

function getStockPriceOverride(stockPrices, ticker) {
    if (stockPrices == null || ticker == null) {
        return null
    }
    const candidates = [ticker, ticker.toUpperCase(), ticker.toLowerCase()]
    for (const key of candidates) {
        if (stockPrices[key] != null) {
            return stockPrices[key]
        }
    }
    return null
}

function firstFiniteValue(values) {
    for (const value of values) {
        if (value == null) {
            continue
        }
        const numberValue = Number(value)
        if (Number.isFinite(numberValue)) {
            return numberValue
        }
    }
    return null
}

function calcUnderlyingPriceForMarketData(pos, stockPrices = {}) {
    const instrument = pos.instrument || {}
    const ticker = instrument.ticker
    const override = getStockPriceOverride(stockPrices, ticker)

    if (['Stock', 'MutualFund', 'OtherSecurity', 'Bond'].includes(instrument.class_name)) {
        return firstFiniteValue([override, pos.price])
    }

    if (instrument.class_name && instrument.class_name.includes('Option')) {
        return firstFiniteValue([
            override,
            pos.und_price,
            pos.underlying_price,
            instrument.und_price,
            instrument.underlying_price
        ])
    }

    return null
}

// forwardCurves is a dictionary of ticker to forward curve data, eg:
// {
//     "AAPL": [
//      ['2025-03-01', 1.01],
//      ['2025-06-01', 1.02]
//     ]
// }
export function createMarketDataObj(markedPositionDicts, asOfDateObj, stockPrices = {}, forwardCurves=null) {
    const uniqueTickers = calcUniqueTickers(markedPositionDicts)
    let mdo = {
        equity: {
            spotPrices: [],
            vols: [],
            forwards: []
        }
    }

    let undPrices = []
    markedPositionDicts.map(pos => {
            const ticker = pos.instrument?.ticker
            if (ticker == null) {
                return
            }
            const underlyingPrice = calcUnderlyingPriceForMarketData(pos, stockPrices)
            if (underlyingPrice != null) {
                undPrices[ticker] = underlyingPrice
            }
        }
    )

    uniqueTickers.map(ut => mdo.equity.spotPrices.push({ticker: ut, spotPrice: undPrices[ut]}))

    // Populate forwards array with forward curve data
    if (forwardCurves) {
        uniqueTickers.forEach(ticker => {
            mdo.equity.forwards.push({
                ticker: ticker,
                forward_curve: forwardCurves[ticker]
            });
        });
    }

    const optionPositionDicts = markedPositionDicts.filter(position => position.instrument.class_name === 'StockOption')

    optionPositionDicts.map(opd => {
        if (opd.instrument.class_name === 'StockOption') {
            const vol = calcMarkedInstrumentVol(opd.instrument, opd.price, mdo, asOfDateObj)

            // ticker
            let tickerVolObj = null
            let iVolObj = mdo.equity.vols.findIndex(volObj => volObj.ticker === opd.instrument.ticker)
            if (iVolObj < 0) {
                let n = mdo.equity.vols.push({ticker: opd.instrument.ticker, expirations: []})
                tickerVolObj = mdo.equity.vols[n - 1]
            } else {
                tickerVolObj = mdo.equity.vols[iVolObj]
            }

            // expiration
            let stripObj = null
            let iStripObj = tickerVolObj.expirations.findIndex(expObj => expObj.expiration === opd.instrument.expiration)
            if (iStripObj < 0) {
                let n = tickerVolObj.expirations.push({expiration: opd.instrument.expiration, strikes: []})
                stripObj = tickerVolObj.expirations[n - 1]
            } else {
                stripObj = tickerVolObj.expirations[iStripObj]
            }

            // strike
            let iStrikeObj = stripObj.strikes.findIndex(expObj => expObj.strike === opd.instrument.strike)
            if (iStrikeObj < 0) {
                let n = stripObj.strikes.push({strike: opd.instrument.strike, vol: vol})
            }
        }
    })
    return mdo
}
